/**
 * sessionStorage-backed tour state persistence — tracks the onboarding tour's
 * progress (step index, awaited action, active flag) and example forecast ID
 * so the tour can resume after page reload. Survives reload within the same tab
 * (sessionStorage) but is absent in a new tab (session-scoped).
 * Storage is injected so callers can test with a fake without touching globals.
 */

import { TOUR_STATE_KEY } from "./storageKeys.js";
import { defaultSessionStorage as _store } from "../util/sessionStorage.js";

export { TOUR_STATE_KEY };

/**
 * Load tour state from storage. Returns null on missing key, parse failure,
 * or any storage access error (private-browsing / quota guard).
 * @param {Storage} storage  Injectable; defaults to sessionStorage (or null when absent).
 * @returns {Object|null} Stored state {exampleForecastId, stepIndex, awaitedAction, active}, or null.
 */
export function readTourState(storage = _store()) {
  try {
    if (!storage) return null;
    const raw = storage.getItem(TOUR_STATE_KEY);
    const p = raw ? JSON.parse(raw) : null;
    return p && typeof p === "object" ? p : null;
  } catch {
    return null;
  }
}

/**
 * Persist tour state to storage. Best-effort: quota / private-mode errors
 * are swallowed — callers should treat the state as a hint, not a guarantee.
 * @param {Object}  state    State object {exampleForecastId, stepIndex, awaitedAction, active}.
 * @param {Storage} storage  Injectable; defaults to sessionStorage (or null when absent).
 */
export function writeTourState(state, storage = _store()) {
  try {
    if (!storage) return;
    storage.setItem(TOUR_STATE_KEY, JSON.stringify(state));
  } catch {
    /* best-effort */
  }
}

/**
 * Clear the tour state from storage (called after the tour completes or is dismissed).
 * @param {Storage} storage  Injectable; defaults to sessionStorage (or null when absent).
 */
export function clearTourState(storage = _store()) {
  try {
    if (!storage) return;
    storage.removeItem(TOUR_STATE_KEY);
  } catch {
    /* best-effort */
  }
}
